// frontend/src/charts/DataTable.jsx
// The 'table' chart type, and the table view behind every chart (the
// accessible alternative to colour). Renders the raw result rows; numbers
// right-aligned with tabular figures so columns line up. Numbers follow the
// chart's number format (7b-2b), exact rather than shortened. 7d-2 (D11):
// columns follow presentation.columns when it is set, a wide table scrolls
// sideways under a sticky header, and with `onSort` the headings are buttons
// that sort by that column (the arrow shows the current order, automatic or
// chosen). Sorting is done by the server through the chart's sort items, so
// the table and the saved chart always agree. 7d-3: columns size to their
// content until a width is set (presentation.columns[].width); with
// `onResize` the right edge of a heading drags the column wider or narrower
// (double-click puts it back to automatic). A column with a width clips long
// text with an ellipsis and shows the full text on hover.
import React, { useRef, useState } from 'react';
import { cellText, columnFormatter, columnLabel, dimensionText } from './format';
import { clampWidth, columnTarget, columnWidths, orderColumns, sameTarget, targetKey } from './tableOrder';

const MAX_ROWS = 500;
const CELL_PADDING = 24;   // px-3 on both sides

/** A cell's content box: free until the column has a width, then exactly that wide. */
function Box({ width, title, right, children }) {
  if (width == null) return children;
  return (
    <div title={title} style={{ width: Math.max(0, width - CELL_PADDING) }}
      className={`overflow-hidden text-ellipsis whitespace-nowrap ${right ? 'text-right' : ''}`}>
      {children}
    </div>
  );
}

export default function DataTable({
  columns = [], rows = [], height = 320, presentation = null, sort = null, onSort = null, onResize = null,
}) {
  const shown = rows.slice(0, MAX_ROWS);
  // Each measure column's numbers: its own format under "Automatic" (13c-4), else the chart's.
  const measureText = new Map(columns.filter((c) => c.kind === 'measure').map((c) => [c.alias, columnFormatter(presentation, c)]));
  // A matrix result's totals marker (7e-1) is not a column to show.
  const ordered = orderColumns(columns.filter((c) => c.kind !== 'totals'), presentation && presentation.columns);
  const widths = columnWidths(presentation && presentation.columns);
  // Cells are read by the column's index in the server's order, whatever the display order.
  const at = new Map(columns.map((c, i) => [c.alias, i]));
  const sortedBy = (c) => (sort && sameTarget(columnTarget(c), sort.target) ? sort.dir : null);

  // While a heading edge is being dragged the width lives here; it is handed
  // to onResize once on release, so the chart's presentation changes once.
  const [dragging, setDragging] = useState(null);   // { key, width }
  const drag = useRef(null);
  const widthOf = (c) => {
    const key = targetKey(columnTarget(c));
    return dragging && dragging.key === key ? dragging.width : widths[key];
  };

  function startResize(e, c) {
    if (e.button !== 0) return;
    const th = e.currentTarget.parentElement;
    const key = targetKey(columnTarget(c));
    drag.current = { key, target: columnTarget(c), startX: e.clientX, startWidth: th.getBoundingClientRect().width };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  }
  function moveResize(e) {
    if (!drag.current) return;
    const w = clampWidth(drag.current.startWidth + (e.clientX - drag.current.startX));
    setDragging({ key: drag.current.key, width: w });
  }
  function endResize(e) {
    if (!drag.current) return;
    const w = clampWidth(drag.current.startWidth + (e.clientX - drag.current.startX));
    const { target } = drag.current;
    drag.current = null;
    setDragging(null);
    if (onResize) onResize(target, w);
  }

  return (
    <div style={{ maxHeight: height }} className="overflow-auto scrollbar-thin">
      <table className="min-w-full border-collapse text-sm">
        <thead className="sticky top-0 z-[1] bg-[color:var(--chart-surface)]">
          <tr>
            {ordered.map((c) => {
              const dir = sortedBy(c);
              const right = c.kind === 'measure';
              const label = columnLabel(c);
              const w = widthOf(c);
              const arrow = dir && <span aria-hidden="true" className="ml-1 text-[10px]">{dir === 'asc' ? '▲' : '▼'}</span>;
              return (
                <th
                  key={c.alias} scope="col"
                  aria-sort={dir ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  style={w != null ? { width: w, minWidth: w, maxWidth: w } : undefined}
                  className={`relative whitespace-nowrap border-b border-[color:var(--chart-grid)] px-3 py-2 font-medium text-[color:var(--chart-text-secondary)] ${right ? 'text-right' : 'text-left'}`}
                >
                  <Box width={w} title={label} right={right}>
                    {onSort ? (
                      <button
                        type="button" onClick={() => onSort(columnTarget(c))}
                        title={dir ? `Sorted ${dir === 'asc' ? 'ascending' : 'descending'}. Click to flip.` : `Sort by ${label}`}
                        className={`-mx-1 inline-flex max-w-full items-center whitespace-nowrap rounded px-1 hover:text-[color:var(--chart-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${
                          dir ? 'text-[color:var(--chart-text)]' : ''}`}
                      >
                        {right && arrow}<span className={w != null ? 'truncate' : ''}>{label}</span>{!right && arrow}
                      </button>
                    ) : label}
                  </Box>
                  {onResize && (
                    <div
                      role="separator" aria-orientation="vertical" aria-label={`Resize ${label}`}
                      title="Drag to resize. Double-click for automatic width."
                      onPointerDown={(e) => startResize(e, c)} onPointerMove={moveResize}
                      onPointerUp={endResize} onPointerCancel={endResize}
                      onDoubleClick={() => onResize(columnTarget(c), null)}
                      className="absolute right-0 top-0 h-full w-2 cursor-col-resize touch-none select-none hover:bg-white/15 active:bg-white/25"
                    />
                  )}
                </th>
              );
            })}
            {/* Takes the spare width, so columns keep their own size (and a dragged one is the only one that moves). */}
            <th aria-hidden="true" className="w-full border-b border-[color:var(--chart-grid)] p-0" />
          </tr>
        </thead>
        <tbody>
          {shown.map((row, r) => (
            <tr key={r} className="border-b border-[color:var(--chart-grid)] last:border-0">
              {ordered.map((c) => {
                const v = row[at.get(c.alias)];
                const right = c.kind === 'measure';
                const text = right ? cellText(v, measureText.get(c.alias)) : dimensionText(v, c);
                const w = widthOf(c);
                return (
                  <td
                    key={c.alias}
                    className={`whitespace-nowrap px-3 py-1.5 text-[color:var(--chart-text)] ${right ? 'text-right tabular-nums' : 'text-left'}`}
                  >
                    <Box width={w} title={w != null ? text : undefined} right={right}>{text}</Box>
                  </td>
                );
              })}
              <td aria-hidden="true" className="p-0" />
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > MAX_ROWS && (
        <p className="px-3 py-2 text-xs text-[color:var(--chart-text-muted)]">
          Showing the first {MAX_ROWS.toLocaleString()} of {rows.length.toLocaleString()} rows.
        </p>
      )}
    </div>
  );
}
